import { useEffect, useState } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import { Show, useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { Sidebar } from "@/components/Sidebar/Sidebar";
import { SettingsModal } from "@/components/Modals/Settings/SettingsModal";
import { TweaksPanel } from "@/components/Modals/Settings/TweaksPanel";
import { useTheme } from "@/hooks/useTheme";
import { useEditModePostMessage } from "@/hooks/useEditModePostMessage";
import { useSentryUser } from "@/hooks/useSentryUser";
import { useAmplitudeUser } from "@/hooks/useAmplitudeUser";

export function ProtectedShell() {
  const params = useParams();
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Sync Clerk user identity into Sentry scope for every event.
  useSentryUser();

  // Sync Clerk user identity into Amplitude for every analytics event.
  useAmplitudeUser();

  // Apply current tweaks to <html> (theme + density).
  useTheme();

  // When the active Clerk org changes (profile switch via SettingsModal),
  // invalidate all React Query caches so profile-scoped data refetches with
  // the new identity. Server middleware (identifyUserProfile) is the source
  // of truth per request — this is how the client picks up the change.
  const { isLoaded, orgId } = useAuth();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!isLoaded) return;
    queryClient.invalidateQueries();
  }, [orgId, isLoaded, queryClient]);

  // TweaksPanel is no longer surfaced from the settings popover (the design
  // dropped that affordance) — it only renders when the parent Claude Design
  // embed activates edit mode via postMessage.
  const editModeRequested = useEditModePostMessage();

  // Wait for Clerk before deciding redirects so a refreshed session doesn't
  // bounce to /sign-in for a frame.
  if (!isLoaded) return null;

  return (
    <>
      <Show when="signed-out">
        <Navigate to="/sign-in" replace />
      </Show>
      <Show when="signed-in">
        <div style={{ height: "100vh", display: "flex", background: "var(--bg)" }}>
          <Sidebar
            activeId={params.id}
            onOpenSettings={() => setSettingsOpen(true)}
          />
          <main
            style={{
              flex: 1,
              minWidth: 0,
              height: "100%",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Outlet />
          </main>
          {settingsOpen && (
            <SettingsModal onClose={() => setSettingsOpen(false)} />
          )}
          {editModeRequested && <TweaksPanel onClose={() => undefined} />}
        </div>
      </Show>
    </>
  );
}
